import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, ChatCircleText, Trash, ThumbsUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    castChallengeVote,
    createComment,
    deleteComment,
    getChallengeVote,
    listComments,
    upvoteComment,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function CommunityPanel({ challengeId }) {
    const { user, login } = useAuth();
    const [comments, setComments] = useState([]);
    const [votes, setVotes] = useState({ up: 0, down: 0, score: 0, my_vote: 0 });
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const refresh = useCallback(async () => {
        if (!challengeId) return;
        setLoading(true);
        try {
            const [c, v] = await Promise.all([
                listComments(challengeId),
                getChallengeVote(challengeId),
            ]);
            setComments(c);
            setVotes(v);
        } catch (e) {
            // Non-fatal: panel falls back to empty state. Surface only unexpected errors.
            if (e?.response?.status && e.response.status >= 500) {
                console.error("Community panel load failed:", e.response.data || e.message);
            }
        } finally {
            setLoading(false);
        }
    }, [challengeId]);

    useEffect(() => { refresh(); }, [refresh]);

    const requireAuth = () => {
        if (!user) {
            toast.message("Sign in with Google to interact", {
                action: { label: "Sign in", onClick: login },
            });
            return false;
        }
        return true;
    };

    const handleVote = async (value) => {
        if (!requireAuth()) return;
        try {
            const res = await castChallengeVote(challengeId, value);
            setVotes(res);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vote failed");
        }
    };

    const handleSubmit = async () => {
        if (!requireAuth()) return;
        if (body.trim().length < 2) return;
        setSubmitting(true);
        try {
            const comment = await createComment(challengeId, body.trim());
            setComments((prev) => [comment, ...prev]);
            setBody("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Comment failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (commentId) => {
        try {
            await deleteComment(challengeId, commentId);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const handleUpvote = async (commentId) => {
        if (!requireAuth()) return;
        try {
            const res = await upvoteComment(challengeId, commentId);
            setComments((prev) =>
                prev.map((c) => (c.id === commentId ? res.comment : c)),
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upvote failed");
        }
    };

    return (
        <div className="border border-white/10 bg-black/30 p-4 space-y-4" data-testid="community-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <ChatCircleText size={16} weight="fill" className="text-fa-cream" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                        Community · {comments.length} comments · score {votes.score >= 0 ? "+" : ""}{votes.score}
                    </span>
                </div>
                <div className="flex items-center gap-1" data-testid="vote-bar">
                    <button
                        type="button"
                        onClick={() => handleVote(1)}
                        data-testid="vote-up-btn"
                        className={`p-1.5 border transition-colors flex items-center gap-1 ${
                            votes.my_vote === 1
                                ? "border-fa-mint/60 bg-fa-mint/10 text-fa-mint"
                                : "border-white/10 text-white/60 hover:border-fa-mint/50 hover:text-fa-mint"
                        }`}
                    >
                        <ArrowUp size={14} weight="bold" />
                        <span className="font-mono text-[11px]" data-testid="vote-up-count">{votes.up}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleVote(-1)}
                        data-testid="vote-down-btn"
                        className={`p-1.5 border transition-colors flex items-center gap-1 ${
                            votes.my_vote === -1
                                ? "border-red-500/60 bg-red-500/10 text-red-400"
                                : "border-white/10 text-white/60 hover:border-red-500/50 hover:text-red-400"
                        }`}
                    >
                        <ArrowDown size={14} weight="bold" />
                        <span className="font-mono text-[11px]" data-testid="vote-down-count">{votes.down}</span>
                    </button>
                </div>
            </div>

            <div className="flex gap-2">
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={user ? "Share your take on this challenge…" : "Sign in to comment"}
                    rows={2}
                    maxLength={1000}
                    data-testid="comment-textarea"
                    disabled={!user || submitting}
                    className="flex-1 bg-black/40 border border-white/10 focus:border-fa-cream/60 outline-none text-white font-mono text-xs p-2 resize-none disabled:opacity-50"
                />
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!user || submitting || body.trim().length < 2}
                    data-testid="comment-submit-btn"
                    className="font-mono text-xs uppercase tracking-widest px-3 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    {submitting ? "…" : "Post"}
                </button>
            </div>

            <div className="space-y-2" data-testid="comments-list">
                {loading && comments.length === 0 ? (
                    <div className="font-mono text-[11px] text-white/40">Loading…</div>
                ) : comments.length === 0 ? (
                    <div className="font-mono text-[11px] text-white/40">No comments yet — be the first.</div>
                ) : (
                    comments.map((c) => (
                        <div
                            key={c.id}
                            className="flex gap-2 border-t border-white/5 pt-2"
                            data-testid={`comment-${c.id}`}
                        >
                            {c.user_picture ? (
                                <img
                                    src={c.user_picture}
                                    alt=""
                                    className="w-7 h-7 rounded-full flex-shrink-0"
                                />
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-fa-violet/40 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                    <Link
                                        to={`/u/${c.user_id}`}
                                        className="font-mono text-[11px] text-fa-cream truncate hover:underline"
                                        data-testid={`comment-author-${c.id}`}
                                    >
                                        {c.user_name}
                                    </Link>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleUpvote(c.id)}
                                            disabled={user?.user_id === c.user_id}
                                            data-testid={`comment-upvote-${c.id}`}
                                            title={
                                                user?.user_id === c.user_id
                                                    ? "Can't upvote your own comment"
                                                    : "Upvote"
                                            }
                                            className="flex items-center gap-1 font-mono text-[10px] text-white/60 hover:text-fa-mint disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ThumbsUp size={11} weight="bold" />
                                            {c.upvotes ?? 0}
                                        </button>
                                        {user?.user_id === c.user_id && (
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(c.id)}
                                                data-testid={`comment-delete-${c.id}`}
                                                className="text-white/40 hover:text-red-400"
                                                title="Delete"
                                            >
                                                <Trash size={11} weight="bold" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="font-mono text-[12px] text-white/80 whitespace-pre-wrap mt-0.5">
                                    {c.body}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
